import React, { useState, useEffect } from 'react';
import { getReviews, saveReview, deleteReview } from '../services/apiService';
import { supabase } from '../services/supabase';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  X, 
  Star, 
  User, 
  MessageSquare, 
  Loader2, 
  Upload 
} from 'lucide-react';

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const { toast, showToast, hideToast } = useToast();
  const [editingReview, setEditingReview] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    rating: 5,
    text: '',
    course: '',
    photo: null,
    video_url: '',
    is_video_review: false,
  });

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      setIsLoading(true);
      const data = await getReviews();
      setReviews(data);
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, photo: file });
      // Use FileReader for preview (data URL, not blob URL)
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      let photoUrl = editingReview?.photo || editingReview?.photo_url || editingReview?.imageUrl;

      if (formData.photo) {
        const fileExt = formData.photo.name.split('.').pop();
        const fileName = `review_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `reviews/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('photos')
          .upload(filePath, formData.photo);

        if (uploadError) {
          console.error('Photo upload error:', uploadError);
          showToast('Failed to upload photo. Please try again.', 'error');
          setSaving(false);
          return;
        }

        const { data: { publicUrl } } = supabase.storage.from('photos').getPublicUrl(filePath);
        photoUrl = publicUrl;
      }

      await saveReview({
        name: formData.name,
        rating: formData.rating,
        text: formData.text,
        course: formData.course,
        photo_url: photoUrl,
        video_url: formData.video_url || null,
        is_video_review: formData.is_video_review || false,
        id: editingReview?.id,
      });
      
      handleCloseForm();
      loadReviews();
      showToast(editingReview ? 'Review updated successfully!' : 'Review added successfully!', 'success');
      // Dispatch event to refresh dashboard
      window.dispatchEvent(new CustomEvent('dataUpdated'));
    } catch (error) {
      console.error('Error saving review:', error);
      showToast('Error saving review. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (review) => {
    setEditingReview(review);
    setFormData({
      name: review.name || '',
      rating: review.rating || 5,
      text: review.text || review.review || '',
      course: review.course || '',
      photo: null,
      video_url: review.video_url || '',
      is_video_review: review.is_video_review || false,
    });
    setPreviewUrl(review.photo || review.photo_url || review.imageUrl || null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      try {
        await deleteReview(id);
        loadReviews();
        showToast('Review deleted successfully!', 'success');
        // Dispatch event to refresh dashboard
        window.dispatchEvent(new CustomEvent('dataUpdated'));
      } catch (error) {
        console.error('Error deleting review:', error);
        showToast('Error deleting review. Please try again.', 'error');
      }
    }
  };

  const handleCloseForm = () => {
      setShowForm(false);
      setEditingReview(null);
      setFormData({ name: '', rating: 5, text: '', course: '', photo: null, video_url: '', is_video_review: false });
      setPreviewUrl(null);
  }

  // Helper for rendering stars
  const renderStars = (count) => (
      <div className="flex text-yellow-400">
          {[...Array(5)].map((_, i) => (
              <Star key={i} size={14} fill={i < count ? "currentColor" : "none"} className={i < count ? "" : "text-slate-300"} />
          ))}
      </div>
  );

  return (
    <div className="space-y-8">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
            <h1 className="text-2xl font-bold text-slate-900">Student Reviews</h1>
            <p className="text-slate-500 text-sm mt-1">Manage testimonials and feedback.</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20"
        >
          <Plus size={18} />
          Add Review
        </button>
      </div>

      {/* REVIEWS GRID */}
      {isLoading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-red-600 mb-4"></div>
          <p className="text-slate-500">Loading reviews...</p>
        </div>
      ) : reviews.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((review) => (
            <div key={review.id} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col h-full">
              
              {/* Review Header */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center shrink-0 border border-slate-100">
                  {(() => {
                    const url = review.photo || review.photo_url || review.imageUrl;
                    const safeUrl = url && !url.startsWith('blob:') ? url : null;
                    return safeUrl ? (
                      <img
                        src={safeUrl}
                        alt={review.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null;
                  })()}
                  {(() => {
                    const url = review.photo || review.photo_url || review.imageUrl;
                    const hasValidPhoto = url && !url.startsWith('blob:');
                    return !hasValidPhoto ? (
                      <User size={20} className="text-slate-400" />
                    ) : null;
                  })()}
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 leading-tight">{review.name}</h3>
                  <div className="mt-1">{renderStars(review.rating || 5)}</div>
                </div>
              </div>

              {/* Review Text */}
              <div className="flex-1 mb-4">
                  <div className="relative pl-3 border-l-2 border-slate-100">
                    <p className="text-slate-600 text-sm line-clamp-4 italic">
                        "{review.text || review.review}"
                    </p>
                  </div>
              </div>

              {/* Footer Actions */}
              <div className="pt-4 border-t border-slate-50 flex justify-between items-center mt-auto">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider bg-slate-50 px-2 py-1 rounded">
                    {review.course || 'Alumni'}
                </span>
                
                <div className="flex gap-2">
                    {!review.is_public && (
                      <button
                          onClick={() => handleEdit(review)}
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                    )}
                    <button
                        onClick={() => handleDelete(review.id)}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
              </div>

            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
            <MessageSquare size={32} />
          </div>
          <h3 className="text-lg font-bold text-slate-900">No reviews yet</h3>
          <p className="text-slate-500 mt-1">Add a testimonial to build trust.</p>
        </div>
      )}

      {/* FORM MODAL */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
            
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
              <h2 className="text-lg font-bold text-slate-900">
                {editingReview ? 'Edit Review' : 'New Review'}
              </h2>
              <button onClick={handleCloseForm} className="text-slate-400 hover:text-red-600 transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* Modal Body (Scrollable) */}
            <div className="p-6 overflow-y-auto">
                <form onSubmit={handleSubmit} className="space-y-5">
                
                {/* Photo Upload */}
                <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                        {previewUrl ? (
                            <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                        ) : (
                            <User size={24} className="text-slate-300" />
                        )}
                    </div>
                    <label className="flex-1">
                        <span className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Student Photo</span>
                        <div className="flex items-center gap-2 w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors relative overflow-hidden group">
                            <Upload size={16} className="text-slate-400 group-hover:text-slate-600" />
                            <span className="text-sm text-slate-500 group-hover:text-slate-700">Choose file...</span>
                            <input type="file" accept="image/*" onChange={handleFileChange} className="absolute inset-0 opacity-0 cursor-pointer" />
                        </div>
                    </label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Name</label>
                        <input
                            type="text"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm"
                            placeholder="Student Name"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Rating</label>
                        <select
                            name="rating"
                            required
                            value={formData.rating}
                            onChange={handleChange}
                            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm"
                        >
                            {[5, 4, 3, 2, 1].map(r => <option key={r} value={r}>{r} Stars</option>)}
                        </select>
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Course / Program</label>
                    <input
                        type="text"
                        name="course"
                        value={formData.course}
                        onChange={handleChange}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm"
                        placeholder="e.g. IELTS Academic"
                    />
                </div>

                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Review Content</label>
                    {/* Video Review Toggle */}
                    <div>
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                name="is_video_review"
                                checked={formData.is_video_review}
                                onChange={(e) => setFormData({ ...formData, is_video_review: e.target.checked })}
                                className="w-5 h-5 text-red-600 border-slate-300 rounded focus:ring-red-600 focus:ring-2"
                            />
                            <span className="text-sm font-semibold text-slate-900">This is a video review</span>
                        </label>
                    </div>

                    {/* Video URL (if video review) */}
                    {formData.is_video_review && (
                        <div>
                            <label className="block text-sm font-semibold text-slate-900 mb-2">YouTube Video URL *</label>
                            <input
                                type="url"
                                name="video_url"
                                required={formData.is_video_review}
                                value={formData.video_url}
                                onChange={handleChange}
                                placeholder="https://www.youtube.com/watch?v=..."
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                            />
                            <p className="text-xs text-slate-500 mt-1">Enter full YouTube URL (e.g., https://www.youtube.com/watch?v=VIDEO_ID)</p>
                        </div>
                    )}

                    <textarea
                        name="text"
                        required={!formData.is_video_review}
                        rows="4"
                        value={formData.text}
                        onChange={handleChange}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm resize-none"
                        placeholder="What did the student say?"
                    />
                </div>

                <div className="pt-2 flex gap-3">
                    <button
                    type="button"
                    onClick={handleCloseForm}
                    disabled={saving}
                    className="flex-1 py-2.5 rounded-lg font-medium text-slate-600 hover:bg-slate-100 transition-colors text-sm"
                    >
                    Cancel
                    </button>
                    <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-slate-900 text-white py-2.5 rounded-lg font-medium hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm flex items-center justify-center gap-2"
                    >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : (editingReview ? 'Update Review' : 'Save Review')}
                    </button>
                </div>
                </form>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          toast={toast}
          hideToast={hideToast}
        />
      )}
    </div>
  );
};

export default AdminReviews;